/* =========================================================================
   Memorial candle — "Light a candle for a loved one." Replaces the hollow
   Daily-Blessing candle (a meaningless per-day boolean) with a reverent act:
   the player names someone who has died, the candle is lit for them today, and
   they pray the traditional "Eternal Rest" prayer. Names accumulate into a
   quiet local "Remembrances" list (a little Book of the Dead).

   REVERENCE RULES (from research, USCCB/EWTN-sourced prayers):
   - This surface is a memorial, NOT a game. No points, no streak, no score,
     no "you broke your prayer streak," no celebratory confetti. Deliberately
     kept clear of the game's scoring/celebration mechanics.
   - The prayers ENTRUST the soul to God's mercy; they never presume the soul's
     state (heaven/hell). "Eternal rest" is exactly this entrusting form.
   - Guest-safe + offline: a single localStorage key, no server needed.

   House pattern: pure logic over an injected KVStore seam (mirrors
   dailyBlessing.ts / progression.ts) so tests inject a stub.
   ========================================================================= */
import { localDateString, type KVStore } from './dailyBlessing';

const KEY = 'catholics.memorial';

/** One remembered soul in the local Book of the Dead. */
export interface Remembrance {
  /** The name the player entered (already trimmed; may be a generic like "a loved one"). */
  name: string;
  firstIso: string; // when first remembered
  lastIso: string; // most recent candle
  times: number; // how many times a candle has been lit for them
}

export interface MemorialState {
  /** Local YYYY-MM-DD of the most recent candle, or null if never lit. */
  lastLitDate: string | null;
  /** The soul the current candle burns for (display on the home chip). */
  lastName: string | null;
  remembrances: Remembrance[];
}

function emptyState(): MemorialState {
  return { lastLitDate: null, lastName: null, remembrances: [] };
}

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readMemorial(store: KVStore | null = deviceStore()): MemorialState {
  try {
    const raw = store?.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<MemorialState>;
      if (p && typeof p === 'object') {
        return {
          lastLitDate: typeof p.lastLitDate === 'string' ? p.lastLitDate : null,
          lastName: typeof p.lastName === 'string' ? p.lastName : null,
          remembrances: Array.isArray(p.remembrances) ? p.remembrances : [],
        };
      }
    }
  } catch {
    /* corrupt/absent — start fresh */
  }
  return emptyState();
}

function writeMemorial(s: MemorialState, store: KVStore | null): void {
  try {
    store?.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode — the candle simply won't persist between reloads */
  }
}

/** Whether a candle has already been lit today (the chip's lit state). */
export function isLitToday(now: Date = new Date(), store: KVStore | null = deviceStore()): boolean {
  return readMemorial(store).lastLitDate === localDateString(now);
}

/** The soul today's candle burns for, or null if not lit today. */
export function currentRemembranceName(
  now: Date = new Date(),
  store: KVStore | null = deviceStore(),
): string | null {
  const s = readMemorial(store);
  return s.lastLitDate === localDateString(now) ? s.lastName : null;
}

/** The Book of the Dead, most-recently-remembered first. PURE read. */
export function getRemembrances(store: KVStore | null = deviceStore()): Remembrance[] {
  return readMemorial(store).remembrances
    .slice()
    .sort((a, b) => (a.lastIso < b.lastIso ? 1 : a.lastIso > b.lastIso ? -1 : 0));
}

/** Trim + bound a raw name entry to something safe to store/render. */
export function normalizeName(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim().slice(0, 60);
}

/**
 * Light today's candle for `name` (blank → an unnamed loved one). Records the
 * remembrance (new or repeat) and stamps today. Returns the updated state.
 * PURE over the injected store — the caller handles telemetry.
 */
export function lightCandleFor(
  rawName: string,
  now: Date = new Date(),
  store: KVStore | null = deviceStore(),
): MemorialState {
  const iso = nowIso(now);
  const today = localDateString(now);
  const name = normalizeName(rawName);
  const s = readMemorial(store);

  if (name) {
    const existing = s.remembrances.find((r) => r.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      existing.lastIso = iso;
      existing.times += 1;
    } else {
      s.remembrances.push({ name, firstIso: iso, lastIso: iso, times: 1 });
    }
  }
  s.lastLitDate = today;
  s.lastName = name || null;
  writeMemorial(s, store);
  return s;
}

/** ISO stamp without relying on Date.now()/new Date() side effects in tests. */
function nowIso(now: Date): string {
  try {
    return now.toISOString();
  } catch {
    return localDateString(now);
  }
}

/* --------------------------- the prayers (verified) ------------------------ */
/* Traditional "Eternal Rest" prayer, verified against USCCB & EWTN primary
   sources. For a single named soul we use the name in the vocative slots and
   the neutral "them/they" (a valid, low-friction form); with no name we use
   "them/they" throughout. These texts are centuries-old and public. */

export interface Prayer {
  title: string;
  /** Lines of the prayer, already personalized. */
  lines: string[];
}

/** The core Eternal Rest prayer, personalized with `name` (blank → unnamed). */
export function eternalRest(rawName = ''): Prayer {
  const name = normalizeName(rawName);
  return {
    title: 'Eternal Rest',
    lines: [
      `Eternal rest grant unto ${name || 'them'}, O Lord,`,
      `and let perpetual light shine upon them.`,
      `May ${name || 'they'} rest in peace. Amen.`,
    ],
  };
}

/* Short reverent factoids shown after the prayer — DYK-style. Drawn from
   established Catholic teaching and entrusting in tone (never presumptive).
   NEEDS DOCTRINAL REVIEW before public deploy (deeper indulgence / All Souls
   specifics were not primary-sourced in the research pass — see handoff). */
export const MEMORIAL_FACTOIDS: ReadonlyArray<string> = [
  'Praying for those who have died is one of the Church’s Spiritual Works of Mercy.',
  'The Church devotes the whole month of November — and especially All Souls’ Day, November 2 — to remembering the faithful departed.',
  'To pray “eternal rest” is to entrust a soul to God’s mercy and ask that his perpetual light shine upon them.',
  'Christians have prayed for their beloved dead since the earliest days of the Church.',
];

/** Deterministic factoid pick (varies by how many souls are remembered — no
    Math.random, which is unavailable in the workflow/test sandbox). */
export function factoidFor(index: number): string {
  const i = ((index % MEMORIAL_FACTOIDS.length) + MEMORIAL_FACTOIDS.length) % MEMORIAL_FACTOIDS.length;
  return MEMORIAL_FACTOIDS[i];
}
